import { WSList, WSText } from "@wingspanhq/fe-component-library";
import {
  ComplianceEntityType,
  GetMissingFieldsLevelParameter
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import {
  queriesComplianceEntities,
  queriesOnboarding,
  queriesPayers,
  queriesPersons
} from "@query";
import { useSession } from "@hooks/useSession";
import { verificationService } from "../../../../shared/services/verificaiton";
import { CardTaxVerificationRequirementRequester } from "../../components/CardTaxVerificationRequirementRequester";
import { FormBusinessInformation } from "../../components/FormBusinessInformation";
import { getProfileDataBusinessV3 } from "../../components/FormBusinessInformation/getProfileDataBusinessV3";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { CreateAccountContext } from "./types";
import { OnboardingModuleCreateAccount } from "../../types";
import { useRedirectToAccountType } from "./useRedirectToAccountType";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const BusinessInformation: React.FC<FlowStep> = ({ onNext, onBack }) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const show1099Message = onboardingModule?.options?.show1099Message;
  const verificationLevel =
    onboardingModule?.options?.verificationLevel || "Tax";

  const session = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({
    personId: session.personId
  });
  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});
  const profileData = useMemo(() => {
    const primaryBusiness = queryComplianceEntities.data?.data?.find(
      entity => !entity.subject && entity.type === ComplianceEntityType.BUSINESS
    );

    return getProfileDataBusinessV3(primaryBusiness);
  }, [queryComplianceEntities.data]);

  const queryMissingData = queriesOnboarding.getMissingFields.useQuery({
    level: verificationLevel as GetMissingFieldsLevelParameter
  });
  const isMismatch = useMemo(
    () =>
      verificationService.getIsBusinessMismatch(
        queryMissingData.data?.requiredFields || []
      ),
    [queryMissingData.data?.requiredFields]
  );

  const queryPayers = queriesPayers.listPayers.useQuery({});
  const payerCompanyName = useMemo(() => {
    const payer = queryPayers.data?.data?.[0];

    if (!payer) {
      return undefined;
    }

    return {
      doingBusinessAs: payer.profile?.company?.doingBusinessAs,
      legalBusinessName: payer.profile?.displayName
    };
  }, [queryPayers.data]);

  useRedirectToAccountType();

  return (
    <WSList gap="2XL">
      <CardTaxVerificationRequirementRequester />

      <FormBusinessInformation
        country={context.accountType?.country || "US"}
        defaultValues={context.businessInformation || profileData}
        onBack={onBack}
        onSubmit={data => {
          setContext(prev => ({ ...prev, businessInformation: data }));
          onNext?.();
        }}
        show1099Message={show1099Message}
        personalEmail={queryPerson.data?.email || undefined}
        mismatch={isMismatch}
        payerCompanyName={payerCompanyName}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};
